import React from 'react';
import cx from 'classnames';
import Box from '../../../../core/layout/box';
import ChoiceBox from '../../../../core/layout/choicebox';
import TextField, { PasswordField, TextArea } from '../../../../core/textfield';
import Checkbox from '../../../../core/checkbox';
import { RadioGroup } from '../../../../core/radio';
import Progress from '../../../../core/progress';
import Button, { ImportantButton } from '../../../../core/button';
import Text from '../../../../core/text';
import Link, { NavLink, LinkList, NavLinks } from '../../../../core/link';
import SelectList from '../../../../core/selectlist';
import Recaptcha from '../../../../core/recaptcha';
import File from '../../../../core/file';
import ObjectEditor from '../../../../core/objecteditor';
import PrintObject from '../../../../core/printobject';
import Forward from '../../../../core/forward';
import Img from '../../../../core/img';
import * as Icons from '../../../../core/icons';
import List from '../../../../core/list';
import { ResultNotifyPrompt } from '../../../../core/form';
import { callWith, isString, isObject } from '../../../../../util';
import Table from '../../../../core/table';
import RenderInTime from '../../../../core/renderintime';
import WindowProps from '../../../../directives/windowProps';

function getClassnames(classNames, componentConfig) {
	cx(classNames && classNames.split(',').map(c => 
		c 
		&& componentConfig 
		&& componentConfig.classNames 
		&& componentConfig.classNames[c]
	))
}

export default function generateComponent(descriptor, generateAPI, componentConfig) {
	
	const {	
		dynamicText, dynamicProps, dynamicValue, findDynamicValue,
		bindTextfield, bindHidden, bindCheckbox, bindRadioGroup, bindSelectList, bindRecaptcha, bindLiveValidate, bindObjectEditor,
		onButtonClick, onRequestScreen, onCheckCondition, 
		errors, suspense, submitDisabled, resultNotify,
		screenHasInit

	} = generateAPI;

	const { 
		key,
		type, 
		field, 
		loopkey, loopvalue, 
		className: clsName,
		defaultValue:incomingDefaultValue, 
		...descriptorProps
	} = descriptor;

	const defaultValue = incomingDefaultValue && dynamicText(incomingDefaultValue);
	const className = getClassnames(clsName, componentConfig);

	const controlProps = {
		...descriptorProps, 
		...className && { className }
	};
	const loopProps = {key:loopkey, i:loopvalue};
	const dControlProps = dynamicProps(controlProps, loopProps);

	if(type === 'textfield'){
		return (
			<TextField 
				key={key}
				disabled={suspense}
				{...dControlProps}
				{...(field && bindTextfield(field, {defaultValue, ...bindLiveValidate(field)}, errors))}
				/>
		)
	} else if(type === 'textarea'){
		return (
			<TextArea 
				key={key}
				disabled={suspense}
				{...dControlProps}
				{...(field && bindTextfield(field, {defaultValue, ...bindLiveValidate(field)}, errors))}
				/>
		)
	} else if(type === 'hidden'){
		return (
			<input 
				key={key}
				type="hidden"
				{...dControlProps}
				{...(field && bindHidden(field, {
					defaultValue
				}))}
				/>
		)
	} else if(type === 'date'){
		return (
			<TextField 
				key={key}
				disabled={suspense}
				type="date"
				InputLabelProps={{shrink: true}}
				{...dControlProps}
				{...(field && bindTextfield(field, {
					defaultValue,
					...bindLiveValidate(field)
				}, errors))}
				/>
		)
	} else if(type === 'time'){
		return (
			<TextField 
				key={key}
				disabled={suspense}
				type="time"
				InputLabelProps={{shrink: true}}
				{...dControlProps}
				{...(field && bindTextfield(field, {
					defaultValue,
					...bindLiveValidate(field)
				}, errors))}
				/>
		)
	} else if(type === 'datetime'){
		return (
			<TextField 
				key={key}
				disabled={suspense}
				type="datetime-local"
				InputLabelProps={{shrink: true}}
				{...dControlProps}
				{...(field && bindTextfield(field, {
					defaultValue,
					...bindLiveValidate(field)
				}, errors))}
				/>
		)
	} else if (type === 'password') {
		return (
			<PasswordField
				key={key} 
				{...dControlProps}
				disabled={suspense}
				{...(field && bindTextfield(field, {
					defaultValue,
					...bindLiveValidate(field)
				}, errors))}
				/>
		);
	} else if (type === 'checkbox') {
		return (
			<Checkbox 
				key={key}
				{...dControlProps}
				disabled={suspense || dControlProps.disabled}
				color="primary"
				{...bindCheckbox(field, {[field]:dynamicValue(incomingDefaultValue)})} />
		);
	} else if (type === 'radio') {
		let { options, ...restControlProps } = dControlProps;
		return (
			<RadioGroup 
				key={key}
				{...restControlProps} 
				options={options && options.map(({label, value, ...rest}) => {
					return ({
						label:label,
						value:value,
						...rest
					})

				})}
				{...restControlProps}
				{...bindRadioGroup(field, {[field]:defaultValue})}
				/>
		);
	} else if (type === 'text') {
		return (
			<Text id={key} key={key} {...controlProps}>
				{
					(dControlProps.children && Array.isArray(dControlProps.children))
						? dControlProps.children.map(
							(c, i) => isString(c) ? c : generateComponent({ ...c, key: `${key}-${i}` }, generateAPI)
						)
						: dControlProps.label.toString()
				}
			</Text>
		);
	} else if (type === 'link') {
		const { service, serviceArgs, screen, windowProps, ...restControlProps} = dControlProps;
		return (
			<Link 
				id={key}
				key={key}
				{...restControlProps}
				onClick={onButtonClick(dControlProps)}>
					{restControlProps.label}
			</Link>
		);
	} else if (type === 'navlink') {
		const { service, serviceArgs, ...restControlProps} = dControlProps;
		return (
			<NavLink 
				key={key}
				{...restControlProps}
				onClick={onButtonClick(dControlProps)}>
					{restControlProps.label}
			</NavLink>
		);
	} else if (type === 'linklist') {
		const {list, options, ...listProps} = dControlProps;
		const items = options || list;
		return (
			<LinkList key={key} {...listProps}>
				{items && items.map(({screen, label, ...item}, li) => (
					<Link 
						key={`linklist-${li}`}
						{...item}
						onClick={screen && callWith(onRequestScreen, screen)}>
							{label}
					</Link>
				))}
			</LinkList>
		);
	} else if (type === 'navlinks') {
		return <NavLinks key={key} {...dControlProps} onLinkClick={onButtonClick} />;
	} else if (type === 'selectlist') {
		const {options, ...restControlProps} = dControlProps;
		const formattedOptions = options || [];
		return (
			<SelectList 
				{...restControlProps}
				key={key} 
				{...bindSelectList(field, {[field]:defaultValue}, formattedOptions)}
				/>
		);
	} else if (type === 'submit') {
		const {formDefault, submitFormData, displaySuspense, submitOnMount, formQuery, ...restControlProps} = dControlProps;
		return (
			<ImportantButton 
				id={`submitbutton${controlProps.service ? `-${controlProps.service}` : ''}`}
				{...restControlProps}
				key={key}
				onClick={onButtonClick({...restControlProps, formDefault, formQuery, submitFormData:submitFormData || true})}
				suspense={suspense && (displaySuspense === undefined || displaySuspense)} 
				disabled={submitDisabled}
				clickOnMount={submitOnMount}
				/>
		);
	} else if (type === 'button') {
		const {formDefault, submitFormData, serviceArgs, ...restControlProps} = dControlProps;
		return (
			<Button
				id={key} 
				{...restControlProps}
				key={key}
				onClick={onButtonClick(dControlProps)}
				/>
		);
	} else if (type === 'box') {
		const { children, direction, ...restControlProps} = dControlProps;
		return (
			<Box key={key} {...restControlProps} type={direction}>
				{children && children.map((item, i) => generateComponent({...item,key:`item-${i}`}, generateAPI))}
			</Box>
		);
	} else if (type === 'paddbox') {
		const { width = '100%', ...restControlProps } = dControlProps;
		return (
			<Box key={key} width={width} {...restControlProps}></Box>
		);
	} else if (type === 'choicebox') {
		const optionsChildren = dControlProps.options.map(({children, i}) => (
			<React.Fragment key={`child-${i}`}>
				{children && children.map((item, ii) => generateComponent({...item, key:`item-${ii}`}, generateAPI))}
			</React.Fragment>
		));

		return (
		<ChoiceBox key={key} {...dControlProps}>{optionsChildren}</ChoiceBox>
		);
	} else if (type === 'progress') {
		return (
			<Progress id={key} key={key} {...dControlProps} />
		);
	} else if (type === 'recaptcha') {
		return (
			<Recaptcha 
				{...dControlProps} 
				{...(field ? {...bindRecaptcha(field)} : {})} 
				key={key} 
				/>
		);
	} else if (type === 'file') {
		return (
			<File 
				{...dControlProps}
				key={key} 
				/>
		);
	} else if (type === 'if') {
		if(onCheckCondition(dControlProps.condition, loopProps)) {
			const children = dControlProps.children || [];
			return (
				<React.Fragment key={key} >{children.map((compDef, i) => {
					const key = `control-${i}`;
					return generateComponent({...compDef, key:key}, generateAPI);
				})}</React.Fragment>
			);
		} else if(dControlProps.else) {
			const children = dControlProps.else;
			return (
				<React.Fragment key={key} >{children.map((compDef, i) => {
					const key = `control-${i}`;
					return generateComponent({...compDef, key:key}, generateAPI);
				})}</React.Fragment>
			);
		} else {
			return null;
		}
		
	} else if (type === 'for') {
		const fieldValue = isString(field) && findDynamicValue(field) || field || []; //eslint-disable-line no-mixed-operators
		const iterateTemplate = controlProps.children; //Children should not have template props populated until they are rendered
		const ListType = dControlProps.listType;

		const applyListWrapper = comp => !!ListType ? <ListType>{comp}</ListType> : comp;
		const applyListItem = comp => !!ListType ? <li>{comp}</li> : comp;
		
		if(fieldValue && Array.isArray(fieldValue)) {
			return applyListWrapper(fieldValue.map((item, i) => {
				return applyListItem(generateComponent({key:`iteration-${i}`, loopvalue:item, ...iterateTemplate}, generateAPI));
			}));
		} else if (fieldValue && isObject(fieldValue)) {
			return applyListWrapper(Object.keys(fieldValue).map((item, i) => {
				return applyListItem(generateComponent({key:`iteration-${i}`,loopkey:item, loopvalue:fieldValue[item], ...iterateTemplate}, generateAPI));
			}));
		}
	} else if (type === 'list') {
		const { children, listType, ...restControlProps} = dControlProps;

		//Children should not have template props populated until they are rendered:
		return (
			<List id={key} key={key} type={listType} {...restControlProps}>
				{controlProps.children && controlProps.children.map((item, i) => generateComponent({...item, key:`item-${i}`}, generateAPI))}
			</List>
		);
	} else if (type === 'prompt') {
		const { notification, ...restControlProps} = dControlProps;
		if(!resultNotify && !notification)return;
		return (
			<ResultNotifyPrompt
				key={key} 
				notification={notification || dynamicProps(resultNotify)}
				{...restControlProps} 
				/>
		);
	} else if (type === 'objecteditor') {
		return (
			<ObjectEditor
				key={key} 
				{...dControlProps}
				{...(field && bindObjectEditor(field, {defaultValue}))}
				/>
		);
	} else if (type === 'printobject') {
		return (
			<PrintObject
				key={key} 
				{...dControlProps}
				/>
		);
	} else if (type === 'img') {
		return (
		<Img id={key} key={key} {...dControlProps}/>
		);
	} else if (type === 'icon') {
		const { icon, ...restControlProps} = dControlProps;
		const Icon = Icons[icon];
		return Icon && (
			<Icon id={key} key={key} {...restControlProps}/>
		);
	} else if (type === 'forward') {
		return <Forward key={key} {...dControlProps}/>
	} else if (type === 'table') {
		const { id, cells } = controlProps;
		const { header, data, className } = dControlProps;

		return header && cells && (
			<Table
				id={id}
				key={key}
				header={header} cells={cells} data={data}
				className={className}
				generateAPI={generateAPI}
				componentConfig={componentConfig}
			/>
		);
	} else if (type === 'renderlater') {
		const { delay, children, trick } = dControlProps;
		return children && (
			<RenderInTime key={key} delay={delay} trick={trick}>
				{children.map(
					(c, i) => generateComponent({ ...c, key: `render-later-${key}-${i}` }, generateAPI)
				)}
			</RenderInTime>
		)
	} else if (type === 'windowprops') {
		return screenHasInit && <WindowProps key={key} {...dControlProps} />;
	}
}